import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Link,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Switch,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_100,
  COLOR_PRIMARY_1_300,
  COLOR_PRIMARY_2,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/colors';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { exchangeRates } from 'src/const';
import { convertCurrency, formatCurrency } from 'src/utils/functions';
import LockIcon from '@mui/icons-material/Lock';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { toggleSubmarketVisibility } from 'src/store/Dashboard/actions';
import { useSelector } from 'react-redux';
const PAGE_SIZE = 30;

const SubmarketsTab = ({
  currency,
  submarkets,
  subscriptionPlan,
  handleOnSampleClick,
  isMobileScreen,
  headerNav,
  setVisibleSubmarkets,
  hoveredSubmarketId,
  setHoveredSubmarketId,
  visibleSubmarkets,
  handleHideSubmarket,
  marketID,
  hiddenSubmarkets,
  parentMarketDetails,
  setShowSubmarketCreationModal,
  setSubmarketCreationSource,
}: {
  currency: string;
  submarkets;
  subscriptionPlan: string;
  handleOnSampleClick;
  isMobileScreen: boolean;
  headerNav: () => JSX.Element;
  setVisibleSubmarkets: (submarkets: any[]) => void;
  hoveredSubmarketId: string | null;
  setHoveredSubmarketId: (hoveredSubmarketId: string | null) => void;
  visibleSubmarkets: any[];
  handleHideSubmarket: (submarketId: string) => void;
  marketID: number;
  hiddenSubmarkets: {
    data: string[];
    loading: boolean;
  };
  parentMarketDetails: {
    country: string;
    city: string;
    district: string;
    displayName: string | null;
  } | null;
  setShowSubmarketCreationModal: (show: boolean) => void;
  setSubmarketCreationSource: (source: 'submarkets-tab' | 'map') => void;
}) => {
  const dispatch = useDispatch();
  const search = useSelector((state: any) => state.dashboard.search);
  const userhasDataAccess = ['expert', 'scale'].includes(subscriptionPlan);
  const currencySymbol = exchangeRates[currency].symbol;
  const ROW_HEIGHT = 52;
  const ROWS_PER_PAGE = 100;
  const [page, setPage] = useState(0);
  const [showHiddenDrawer, setShowHiddenDrawer] = useState(false);
  const [selectedBedroom, setSelectedBedroom] = useState<string | number>('any');

  const filterSubmarketsByBedroom = (submarkets: any[], bedroom: string | number) => {
    if (bedroom === 'any') return submarkets;

    return submarkets.map(submarket => ({
      ...submarket,
      occupancy: submarket.occupancy_by_bedroom?.[bedroom] || submarket.occupancy,
      adr: submarket.adr_by_bedroom?.[bedroom] || submarket.adr,
      supply: submarket.supply_by_bedroom?.[bedroom] || submarket.supply,
      revenue: submarket.revenue_by_bedroom?.[bedroom] || submarket.revenue,
    }));
  };

  useEffect(() => {
    if (submarkets) {
      const filteredSubmarkets = submarkets
        .filter(s => !hiddenSubmarkets.data.includes(String(s.submarketId)))
        .map(submarket => filterSubmarketsByBedroom([submarket], selectedBedroom)[0]);
      setVisibleSubmarkets(filteredSubmarkets);
    }
    return () => {
      setVisibleSubmarkets([]);
    };
  }, [submarkets, selectedBedroom]);

  useEffect(() => {
    if (hoveredSubmarketId && submarkets) {
      const index = submarkets.findIndex(s => s.submarketId === hoveredSubmarketId);
      if (index !== -1) {
        const targetPage = Math.floor(index / ROWS_PER_PAGE);
        setPage(targetPage);

        // Wait for page change to complete before scrolling
        setTimeout(() => {
          const gridViewport = document.querySelector('.MuiDataGrid-virtualScroller');
          if (gridViewport) {
            const scrollPosition = (index % ROWS_PER_PAGE) * ROW_HEIGHT;
            gridViewport.scrollTo({
              top: scrollPosition - gridViewport.clientHeight / 2 + ROW_HEIGHT / 2,
              behavior: 'smooth',
            });
          }
        }, 0);
      }
    }
  }, [hoveredSubmarketId, submarkets]);

  const handleShowSubmarket = (submarketId: string) => {
    const submarketToShow = submarkets.find(s => s.submarketId === submarketId);
    if (submarketToShow) {
      setVisibleSubmarkets([...visibleSubmarkets, submarketToShow]);
    }
    dispatch(toggleSubmarketVisibility(marketID, [submarketId], 'show'));
  };

  const handleShowAllSubmarkets = () => {
    setVisibleSubmarkets(submarkets);
    dispatch(toggleSubmarketVisibility(marketID, [], 'show_all'));
  };

  const columns: GridColDef[] = [
    {
      field: 'submarketName',
      headerName: 'Name',
      flex: 1,
      minWidth: 250,
      renderCell: params => (
        <Link
          target="_blank"
          href={params.row.submarketUrl}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            color: COLOR_PRIMARY_2,
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {params.value}
          <OpenInNewRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
          {params.row.verified_tier == 'C' && <Chip label="Custom" color="success" size="small" />}
        </Link>
      ),
    },
    {
      field: 'supply',
      headerName: 'Active Listings',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value ? params.value : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'occupancy',
      headerName: 'Occupancy Rate',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value ? `${params.value}%` : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'adr',
      headerName: 'Daily Rate',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value
          ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, params.value)))}`
          : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'revenue',
      headerName: 'Revenue Potential',
      flex: 1,
      minWidth: 150,
      renderCell: params => {
        const value = params.value
          ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, params.value)))}`
          : '-';
        return !userhasDataAccess ? (
          <IconButton onClick={() => handleOnSampleClick('expert')}>
            <LockIcon color="primary" />
          </IconButton>
        ) : (
          <Typography sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>{value}</Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: params => {
        return (
          <IconButton
            onClick={() => handleHideSubmarket(params.row.submarketId)}
            sx={{ color: COLOR_TEXT_2 }}
          >
            <VisibilityOffIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleSortChange = (model: any) => {
    if (!submarkets) return;

    const sortedSubmarkets = [...submarkets].sort((a, b) => {
      const sortField = model[0]?.field;
      const sortDirection = model[0]?.sort === 'desc' ? -1 : 1;

      if (!sortField) return 0;

      // Handle numeric fields
      if (['occupancy', 'adr', 'revenue', 'supply'].includes(sortField)) {
        return (a[sortField] - b[sortField]) * sortDirection;
      }

      // Handle string fields
      if (sortField === 'submarketName') {
        return a[sortField].localeCompare(b[sortField]) * sortDirection;
      }

      return 0;
    });

    setVisibleSubmarkets(sortedSubmarkets);
  };

  if (submarkets === 'loading') {
    return (
      <Box
        display={'flex'}
        justifyContent="center"
        alignItems="center"
        sx={{ height: '200px', width: '100%' }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!submarkets) {
    return (
      <Box
        display={'flex'}
        justifyContent="center"
        alignItems="center"
        sx={{ height: '200px', width: '100%' }}
      >
        <Typography variant="body1" color="gray">
          No Submarkets available
        </Typography>
      </Box>
    );
  }

  const getHeaderTitle = () => {
    let country = '0';
    let city = '0';
    let district = '';
    let displayName = '';
    if (parentMarketDetails) {
      country = parentMarketDetails.country
        ? parentMarketDetails.country?.replace(/\s/g, '-').toLocaleLowerCase()
        : '0';
      city = parentMarketDetails.city ? parentMarketDetails.city.toLocaleLowerCase() : '0';
      district = parentMarketDetails.district.toLocaleLowerCase();
      displayName = parentMarketDetails.displayName || parentMarketDetails.district;
    } else {
      country = search.data.country?.replace(/\s/g, '-').toLocaleLowerCase() || '0';
      city = search.data.city?.toLocaleLowerCase() || '0';
      district = search.data.name?.toLocaleLowerCase();
      displayName = search.data.displayName || search.data.name;
    }

    const marketUrl = `/airbnb-data/${country}/${city}/${district}`;

    return (
      <Link underline="hover" color="primary" href={marketUrl} target="_blank">
        {displayName}
      </Link>
    );
  };

  return (
    <Grid
      container
      sx={{
        p: '20px',
        pb: isMobileScreen ? '120px' : '20px',
        height: isMobileScreen ? '90vh' : '80vh',
      }}
      id="submarketContainerGrid"
    >
      {isMobileScreen && headerNav && <Box sx={{ my: '20px' }}>{headerNav()}</Box>}

      {/* Header */}
      <Grid item xs={12} md={6} sx={{ mb: 2 }}>
        <Typography variant="h6">Submarket Analysis of {getHeaderTitle()}</Typography>
      </Grid>

      <Grid container justifyContent="flex-end" item xs={12} sx={{ width: '100%' }} gap={2}>
        <Grid item sx={{ mb: 2 }}>
          <FormControl sx={{ width: '120px' }} size="small">
            <InputLabel>Bedroom</InputLabel>
            <Select
              value={selectedBedroom}
              label="Bedroom"
              onChange={event => setSelectedBedroom(event.target.value)}
            >
              {[
                ['Any', 'any'],
                ['Studio', 0],
                [1, 1],
                [2, 2],
                [3, 3],
                [4, 4],
                [5, 5],
                ['6+', 6],
              ].map(bedroom => (
                <MenuItem value={bedroom[1]}>{bedroom[0]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {hiddenSubmarkets.data.length > 0 && (
          <Grid item>
            <Box
              sx={{
                width: '100%',
                mb: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Badge
                badgeContent={
                  hiddenSubmarkets.loading ? (
                    <CircularProgress size={10} sx={{ color: 'white' }} />
                  ) : (
                    hiddenSubmarkets.data.length
                  )
                }
                color="primary"
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowHiddenDrawer(true)}
                  startIcon={<VisibilityOffIcon />}
                  disabled={hiddenSubmarkets.loading}
                >
                  Hidden Submarkets
                </Button>
              </Badge>
            </Box>
          </Grid>
        )}

        <Grid item>
          <Button
            variant="outlined"
            onClick={() => {
              setShowSubmarketCreationModal(true);
              setSubmarketCreationSource('submarkets-tab');
            }}
          >
            + Create Submarket
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ width: '100%', height: 'calc(100% - 60px)' }}>
        <DataGrid
          rows={visibleSubmarkets}
          columns={columns}
          disableSelectionOnClick
          getRowId={row => row.submarketId}
          pageSize={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          page={page}
          onPageChange={newPage => setPage(newPage)}
          onSortModelChange={handleSortChange}
          getRowClassName={params => {
            return params.row.submarketId === hoveredSubmarketId ? 'highlighted-row' : '';
          }}
          componentsProps={{
            row: {
              onMouseEnter: e => {
                const id = e.currentTarget.getAttribute('data-id');
                id && setHoveredSubmarketId(id);
              },
              onMouseLeave: () => {
                setHoveredSubmarketId(null);
              },
            },
          }}
          sx={{
            backgroundColor: 'white',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .highlighted-row': {
              backgroundColor: `${COLOR_PRIMARY_1_100} !important`,
            },
            '& .MuiDataGrid-cell': {
              fontWeight: 500,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 500,
            },
          }}
        />
      </Box>

      <Drawer anchor="right" open={showHiddenDrawer} onClose={() => setShowHiddenDrawer(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">
              Hidden Submarkets ({hiddenSubmarkets.loading ? '...' : hiddenSubmarkets.data.length})
            </Typography>
            <IconButton onClick={() => setShowHiddenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {hiddenSubmarkets.data.length > 0 ? (
            <>
              <Button
                variant="contained"
                fullWidth
                onClick={handleShowAllSubmarkets}
                sx={{ mb: 2 }}
              >
                Show All Submarkets
              </Button>
              <List>
                {submarkets
                  .filter(s => !visibleSubmarkets.some(vs => vs.submarketId === s.submarketId))
                  .map(submarket => (
                    <ListItem
                      key={submarket.submarketId}
                      secondaryAction={
                        <IconButton onClick={() => handleShowSubmarket(submarket.submarketId)}>
                          <VisibilityIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={submarket.submarketName} />
                    </ListItem>
                  ))}
              </List>
            </>
          ) : (
            <Typography color="text.secondary" align="center">
              No hidden submarkets
            </Typography>
          )}
        </Box>
      </Drawer>
    </Grid>
  );
};

export default SubmarketsTab;
